/**
 * @license
 * @copyright Copyright Motili Inc., 2026 All Rights Reserved
 */

import isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import filter from 'lodash/filter';
import _forEach from 'lodash/forEach';

import { trim } from 'lodash';
import {
    DOCUMENT_TYPE,
    WORK_ORDER_TYPE,
} from 'common/stores/redux/MetaStore/MetaStoreApi.types';

import * as Utils from 'common/utils/Utils';
import {
    includesOrderedEquipment,
    sumPayoutTotal,
} from 'common/utils/line-item-utils';
import { promiseSequence } from 'common/utils/Utils';
import { isVideo } from 'common/components/document/DocumentRequirementsPhotos/DocumentRequirementsPhotosUploaded';
import * as ApiService from './ApiService';
import * as DocumentService from './DocumentService';
import { isMediaRequirementType } from '../utils/requirements/requirement-utils';

const baseUrl = 'workOrders';

/*
 * -=-=-=-=-=-=-=-=-= REST api calls =-=-=-=-=-=-=-=--=-=-=-
 */

export function find(query) {
    return ApiService.post(true, `${baseUrl}/search`, query);
}

export function findInDocument(query) {
    return ApiService.post(true, 'documents/search', query);
}

export function findWorkOrdersWithZip(query) {
    return ApiService.post(true, `${baseUrl}/searchWithZip`, query);
}

export function findWorkOrdersWithZipCount(query) {
    return ApiService.post(true, `${baseUrl}/searchWithZip/count`, query);
}

/**
 * retrieves a work order
 * @param {number} id
 * @param {object} [query]
 * @returns {Promise<Object>}
 */
export function fetchWorkOrder(id, query) {
    return ApiService.get(true, `${baseUrl}/${id}`, query);
}

/**
 * retrieves a work order by custom filter
 * @param {number} id
 * @param {string} filterId
 * @returns {Promise<Object>}
 */
export function fetchWorkOrderByFilterId(id, filterId) {
    return ApiService.get(true, `${baseUrl}/${id}/filter/${filterId}`);
}

/**
 * retrieves line items directly for a work order
 * @param {number} id
 * @param {object} [filter] - loopback filter
 * @returns {Promise<Array>}
 */

/**
 * retrieves a work order
 * @param {number} id
 * @param {object} [query]
 * @returns {Promise<Object>}
 */
export function fetchWorkOrderWithPreferredContractor(id, query) {
    return ApiService.get(
        true,
        `${baseUrl}/findByIdWithPreferredContractor/${id}`,
        query
    );
}

export function fetchWorkOrderSnapshot(id, fetchDispatchingData = false) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/snapshot`,
        {},
        { fetchDispatchingData }
    );
}

export function fetchWorkOrderDispatchEligibility(id) {
    return ApiService.get(true, `${baseUrl}/${id}/dispatch-eligibility`);
}

export function fetchWorkOrders(query) {
    return ApiService.post(true, `${baseUrl}/search`, query);
}

/**
 *
 * @param {Date<Object>} [completedAfter]
 * @param {Date<Object>} [completedBefore]
 * @param {Boolean} underClip
 * @returns {Promise<Object>} workOrderIds[]
 */
export function fetchCompletedWorkOrders(
    completedAfter,
    completedBefore,
    underClip
) {
    const query = {
        underClip,
    };
    if (completedAfter) {
        query.completedAfter = completedAfter;
    }
    if (completedBefore) {
        query.completedBefore = completedBefore;
    }
    return ApiService.post(
        true,
        `${baseUrl}/searchCompleted`,
        undefined,
        query
    );
}

export function count(query) {
    return ApiService.post(true, `${baseUrl}/search/count`, query);
}

function trimWorkOrder(workOrder) {
    return {
        ...workOrder,
        extReferenceNumber1: trim(workOrder.extReferenceNumber1),
        extReferenceNumber2: trim(workOrder.extReferenceNumber2),
        extPoNumber: trim(workOrder.extPoNumber),
        instructions: {
            gateCode: trim(workOrder.instructions.gateCode),
            lockboxCode: trim(workOrder.instructions.lockboxCode),
            special: trim(workOrder.instructions.special),
        },
        contacts: workOrder.contacts.map(c => ({
            ...c,
            email: trim(c.email),
            name: trim(c.name),
        })),
    };
}

export function create(workOrder) {
    return ApiService.post(true, `${baseUrl}`, trimWorkOrder(workOrder));
}

export function createFromDocument(data) {
    return ApiService.post(true, `${baseUrl}/createFromDocument`, data);
}

export function bulkCreate(request) {
    return ApiService.post(true, `${baseUrl}/bulkCreate`, request);
}

export function patch(id, data) {
    return ApiService.patch(true, `${baseUrl}/${id}`, data);
}

/**
 * Create a new note for the workOrder
 * @param {*} workOrderId
 * @param {*} data
 * @param {*} addToComment
 * @param {*} editable
 */
export function createNote(workOrderId, data, addToComment, editable) {
    return ApiService.post(true, `${baseUrl}/${workOrderId}/notes`, data, {
        addToComment,
        editable,
    });
}

/**
 * Update a note for the workOrder
 * @param {*} workOrderId
 * @param {*} noteId
 * @param {*} data
 */
export function updateNote(workOrderId, noteId, data) {
    return ApiService.put(
        true,
        `${baseUrl}/${workOrderId}/notes/${noteId}`,
        data
    );
}

/**
 * Create a new note for the workOrder
 * @param {*} workOrderId
 * @param {*} data
 */
export function createComment(workOrderId, data) {
    return ApiService.post(true, `${baseUrl}/${workOrderId}/comments`, data);
}

/**
 * Update a note for the workOrder
 * @param {*} quoteId
 * @param {*} commentId
 * @param {*} data
 */
export function updateComment(quoteId, commentId, data) {
    return ApiService.put(
        true,
        `${baseUrl}/${quoteId}/comments/${commentId}`,
        data
    );
}

/**
 * create a line items
 * @param {number} id
 * @param {array} lineItems
 * @returns {Promise<Object>}
 */
export function bulkCreateLineItems(id, lineItems) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/bulkCreate`,
        lineItems
    );
}

/**
 * create a line item
 * @param {number} id
 * @param {object} lineItem
 * @returns {Promise<Object>}
 */
export function createLineItem(id, lineItem) {
    return ApiService.post(true, `${baseUrl}/${id}/lineItems`, lineItem);
}

/**
 * update a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} lineItem
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function updateLineItem(id, lineItemId, lineItem, finance) {
    const query = {};
    if (finance) {
        query.finance = finance;
    }
    return ApiService.put(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}`,
        lineItem,
        query
    );
}

/**
 * adjust a line item
 * @param {number} id
 * @param {number} lineItemId
 * @param {object} adjustment
 * @param {boolean} [finance]
 * @returns {Promise<Object>}
 */
export function adjustFinanceLineItem(id, lineItemId, adjustment, finance) {
    const query = {};
    if (finance) {
        query.finance = finance;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/adjustFinance`,
        adjustment,
        query
    );
}

export function bulkAdjustLineItems(
    workOrderId,
    bulkAdjustment,
    adjustmentReason,
    adjustmentReasonNote
) {
    const query = {
        bulkAdjustment,
        adjustmentReason,
        adjustmentReasonNote,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/lineItems/bulkAdjustment`,
        {},
        query
    );
}

/**
 * approve a line item
 * @param {Number} id
 * @param {Number} lineItemId
 */
export function approveLineItem(id, lineItemId, reason) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/approve`,
        {},
        { reason }
    );
}

/**
 * unapprove a line item
 * @param {Number} id
 * @param {Number} lineItemId
 */
export function unapproveLineItem(id, lineItemId, reason) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/unapprove`,
        {},
        { reason }
    );
}

/**
 * substitute a line item
 * @param {Number} id
 * @param {Number} lineItemId
 * @param {String} newLineItem
 * @param {boolean} isCustom
 * @returns {Promise}
 */
export function substituteLineItem(id, lineItemId, newLineItem, isCustom) {
    const query = {
        isCustom,
    };
    const body = {
        sku: newLineItem.sku,
    };
    if (isCustom) {
        body.suppliedDescription = newLineItem.suppliedDescription;
    }
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/${lineItemId}/substitute`,
        body,
        query
    );
}

/**
 * deletes a line item
 * @param {number} id
 * @param {number} lineItemId
 * @returns {Promise<Object>}
 */
export function deleteLineItem(id, lineItemId) {
    return ApiService.del(`${baseUrl}/${id}/lineItems/${lineItemId}`);
}

export function bulkRemoveLineItems(id, lineItemIds) {
    return ApiService.post(true, `${baseUrl}/${id}/lineItems/bulkDelete`, {
        lineItemIds,
    });
}

/**
 * @param {Number} id
 * @param {String} typeId
 * @param {String} [description]
 * @param {String} url
 * @param {Object} file
 * @param {String} file.name
 * @param {String} file.type
 * @param {Number} file.size
 * @param {Object} result
 */
export function createPhoto(id, requirement) {
    return ApiService.post(true, `${baseUrl}/${id}/requirements`, requirement);
}
export function reUploadRejectedWorkOrderRequirement(id, fk, requirement) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/requirements/${fk}/reUploadRejectedRequirement`,
        requirement
    );
}

/**
 * @param {Number} id
 * @param {String} photoId
 */
export function deletePhoto(id, photoId) {
    return ApiService.del(`${baseUrl}/${id}/requirements/${photoId}`);
}

export function editUploadedPhotoRequirement(id, photoId, updateRequirement) {
    return ApiService.put(true, `${baseUrl}/${id}/requirements/${photoId}`, {
        ...updateRequirement,
    });
}

export function getPhotoRequirementApprovalHistory(id, requirementId) {
    return ApiService.get(
        true,
        `${baseUrl}/${id}/requirements/${requirementId}/photoRequirementApprovalHistory`
    );
}

export function saveApprovalRequirement(id, requirementId, approval) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/requirements/${requirementId}/approvalRequirement`,
        {
            ...approval,
        }
    );
}
export function bulkApprovalRequirement(id, requirementIds, approval) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/requirements/bulkApprovalRequirement`,
        {
            woReqIds: requirementIds,
            approval,
        }
    );
}
export function savePhotoAttributes(id, photoId, updateRequirement) {
    return ApiService.put(true, `${baseUrl}/${id}/requirements/${photoId}`, {
        ...updateRequirement,
    });
}

export function revertChecklist(checklist, reason) {
    return ApiService.post(
        true,
        `${baseUrl}/${checklist.workOrderId}/requirements/${checklist.id}/revertChecklist`,
        {
            reason,
        }
    );
}

export function saveChecklist(checklist, sections) {
    return ApiService.put(
        true,
        `${baseUrl}/${checklist.workOrderId}/requirements/${checklist.id}`,
        {
            requirement: {
                ...checklist.requirement,
                sections,
            },
        }
    );
}

/**
 * @param {Number} workOrderId
 * @param {Number} requirementId
 * @param {String} reason
 */
export function completeChecklist(workOrderId, requirementId, reason) {
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/requirements/${requirementId}/completeChecklist`,
        {},
        { reason }
    );
}

/**
 * @param {Number} workOrderId
 * @param {Number} requirementId
 * @param {String} reason
 */
export function removeChecklist(workOrderId, requirementId, reason) {
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/requirements/${requirementId}/removeChecklist`,
        {},
        { reason }
    );
}

/**
 * @param {Number} workOrderId
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export function addChecklists(workOrderId, data) {
    return ApiService.put(
        true,
        `${baseUrl}/${workOrderId}/requirements/addChecklists`,
        { data }
    );
}

export function getDelayReasonTypes() {
    return ApiService.get(true, `${baseUrl}/delayReasonTypes`);
}

export function getUnassignReasonTypes() {
    return ApiService.get(true, `${baseUrl}/unassignReasonTypes`);
}

export function getRescheduleReasonTypes() {
    return ApiService.get(true, `${baseUrl}/rescheduleReasonTypes`);
}

export function getCreateQuoteFromWorkOrderReasonTypes() {
    return ApiService.get(
        true,
        `${baseUrl}/createQuoteFromWorkOrderReasonTypes`
    );
}

export function getWaitingForApprovalReasonTypes() {
    return ApiService.get(true, `${baseUrl}/waitingForApprovalReasonTypes`);
}

export function updateContact(workOrder, contact, occupied, id) {
    // either add to or update WorkOrder-contacts table
    let contactApi = null;
    if (workOrder.contacts.length && id) {
        contactApi = ApiService.put(
            true,
            `${baseUrl}/${workOrder.id}/contacts/${id}`,
            contact
        );
    } else {
        contactApi = ApiService.post(
            true,
            `${baseUrl}/${workOrder.id}/contacts`,
            contact
        );
    }

    const requests = [
        () => contactApi,
        () => ApiService.patch(true, `${baseUrl}/${workOrder.id}`, occupied),
    ];
    return promiseSequence(requests);
}

export function deleteContact(workOrderId, id) {
    return ApiService.del(`${baseUrl}/${workOrderId}/contacts/${id}`);
}

/*
 * -=-=-=-=-=-=-=-=-= ACTION api calls =-=-=-=-=-=-=-=--=-=-=-
 */

/**
 * work order fulfilled action
 * @param {Number} id
 * @returns {Promise<Object>}
 */
export function fulfilled(id) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/fulfilled`,
        undefined,
        undefined
    );
}

/**
 *
 * @param {string} id
 * @param {Object} _schedule
 * @param {String} _schedule.startDateTime
 * @param {string} _schedule.endDateTime
 * @param {String} _schedule.type
 * @param {string} _schedule.eventId
 * @param {string} [reason]
 * @param {string} [followupActionId]
 * @param {number} [assignContractorId]
 * @param {number} [assignContractorTeamMemberId]
 * @returns {Promise<Object>}
 */
export function schedule(
    id,
    _schedule,
    reason,
    reasonNote,
    followupActionId,
    assignContractorId,
    assignContractorTeamMemberId,
    workers = undefined
) {
    const query = {};
    if (reason) {
        query.reason = reason;
    }
    if (reasonNote) {
        query.reasonNote = reasonNote;
    }
    if (followupActionId) {
        query.followupActionId = followupActionId;
    }
    if (assignContractorId) {
        query.assignContractorId = assignContractorId;
    }
    if (assignContractorTeamMemberId) {
        query.assignContractorTeamMemberId = assignContractorTeamMemberId;
    }
    if (workers && Array.isArray(workers)) {
        query.workerIds = workers;
    }
    return ApiService.post(true, `${baseUrl}/${id}/schedule`, _schedule, query);
}

/**
 * @function reschedule
 * @param  {number} id             {document id}
 * @param  {object} _schedule  {moment time object}
 * @param  {string} reasonType     {reschedule reason type}
 * @param  {string} reasonComments {reason comments}
 * @param  {boolean} isNCF {is no contractor found}
 * @return {Promise}
 */
export function reschedule(id, _schedule, reasonType, reasonComments, isNCF) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/reschedule`,
        { type: 'MOTILI_SCHEDULE', startDateTime: _schedule, isNCF },
        { reasonType, reasonComments }
    );
}

/**
 *
 * @param {number} id
 * @param {number} contractorId
 * @param {number} contractorTeamMemberId
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function assign(
    id,
    contractorId,
    contractorTeamMemberId,
    reason,
    reasonNote,
    workers = undefined,
    continueAssigned = undefined
) {
    const query = {
        contractorId,
        contractorTeamMemberId,
        reason,
        reasonNote,
        ...(continueAssigned && { continueAssigned: true }),
    };
    const data =
        workers && Array.isArray(workers) ? { workerIds: workers } : {};
    return ApiService.post(true, `${baseUrl}/${id}/assign`, data, query);
}

/**
 *
 * @param {number} id
 * @param {number} contractorId
 * @param {number} contractorTeamMemberId
 * @param {string} reason
 * @param {string} reasonNote
 * @returns {Promise<Object>}
 */
export function reAssign(
    id,
    contractorId,
    contractorTeamMemberId,
    reason,
    reasonNote,
    workers,
    continueAssigned = undefined
) {
    const query = {
        contractorId,
        contractorTeamMemberId,
        reason,
        reasonNote,
        ...(continueAssigned && { continueAssigned: true }),
    };
    const data =
        workers && Array.isArray(workers) ? { workerIds: workers } : {};
    return ApiService.post(true, `${baseUrl}/${id}/reassign`, data, query);
}

/**
 *
 * @param {number} id
 * @param {string} quoteTypeId
 * @param {array} reasons
 * @returns {Promise<Object>}
 */
export function complete(id, quoteTypeId, reasons) {
    const query = {
        id,
        quoteTypeId,
        reasons,
    };
    return ApiService.post(true, `${baseUrl}/${id}/complete`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function reOpen(id, reason) {
    const query = {
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/reopen`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @param {string} reasonType
 * @param {string} reasonTypeLabel
 * @param {string} note
 * @param {boolean} removeSchedule
 * @returns {Promise<Object>}
 */
export function unAssign(
    id,
    reason,
    reasonType,
    reasonTypeLabel,
    note,
    removeSchedule
) {
    const query = {
        reason,
        reasonType,
        reasonTypeLabel,
        note,
        removeSchedule,
    };
    return ApiService.post(true, `${baseUrl}/${id}/unassign`, {}, query);
}

/**
 *
 * @param {number} id workOrderId
 * @param {object} checkIn
 * @param {string} checkIn.checkInType
 * @param {string} checkIn.checkInReason
 * @param {string} checkIn.checkedInAt
 * @param {string} checkIn.checkedOutAt
 * @param {object} checkIn.checkInCoords
 * @param {number} checkIn.checkInCoords.lat
 * @param {number} checkIn.checkInCoords.lng
 * @param {number} [checkIn.checkedInUserId] only needed for Admin
 * @param {number} checkIn.checkedInUser
 * @param {number} checkIn.timezoneId
 * @returns {Promise<Object>}
 */
export function checkIn(id, checkIn) {
    return ApiService.post(true, `${baseUrl}/${id}/checkinsv2`, checkIn);
}

/**
 *
 * @param {number} id
 * @param {number} checkInId
 * @param {object} checkOut
 * @param {string} checkOut.checkOutReason
 * @param {string} checkOut.checkedOutAt
 * @param {number} checkOut.timezoneId
 * @param {object} checkOut.checkOutCoords
 * @param {number} checkOut.checkOutCoords.lat
 * @param {number} checkOut.checkOutCoords.lng
 * @returns {Promise<Object>}
 */
export function checkOut(id, checkInId, checkOut) {
    return ApiService.put(
        true,
        `${baseUrl}/${id}/checkinsv2/${checkInId}`,
        checkOut
    );
}

export function adminNoAccountCheckOut(id, checkOut) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/adminNoAccountCheckOut`,
        checkOut
    );
}

/**
 *
 * @param {number | string} id
 * @param {object} data
 * @param {string} [data.quoteTypeId]
 * @param {boolean} [data.doneOnBehalfOf]
 * @param {string} [data.onBehalfOfReason]
 * @param {array} data.reasons
 * @param {object} data.permitResponses
 * @param {date} data.installDate
 * @param {boolean} data.createRepairWO
 */
export function submit(id, data) {
    return ApiService.post(true, `${baseUrl}/${id}/submit`, data, {});
}

/**
 *
 * @param {number | string} id
 * @param {object} data
 * @param {string} [data.quoteTypeId]
 * @param {boolean} [data.doneOnBehalfOf]
 * @param {string} [data.onBehalfOfReason]
 * @param {array} data.reasons
 * @param {object} data.permitResponses
 * @param {date} data.installDate
 * @param {boolean} data.createRepairWO
 */
export function identifyLHSRisk(id, data) {
    return ApiService.post(true, `${baseUrl}/${id}/identifyLHSRisk`, data, {});
}

/**
 * Apply or update the Potential Damage exception flag on a work order (CS-3995).
 * @param {number} id work order id
 * @param {Object} data { potentialDamage: { riskType, riskReasons, otherReason, assigneeId, notes } | null }
 */
export function identifyPotentialDamage(id, data) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/identifyPotentialDamage`,
        data,
        {}
    );
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @param {string} reasonType
 * @param {string} delayEndDateTime
 * @returns {Promise<Object>}
 */
export function delay(id, reason, reasonType, delayEndDateTime) {
    const query = {
        reason,
        reasonType,
        delayEndDateTime,
    };
    return ApiService.post(true, `${baseUrl}/${id}/delay`, {}, query);
}

export function delayDispatching(id, holdType, delayEndDateTime, reason) {
    const query = {
        holdType,
        delayEndDateTime,
        reason,
    };
    return ApiService.post(
        true,
        `${baseUrl}/${id}/delayDispatching`,
        {},
        query
    );
}

export function editDispatching(id, holdType, delayEndDateTime, reason) {
    const query = {
        holdType,
        delayEndDateTime,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/editDispatching`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @param {string} reasonType
 * @param {string} quoteTypeId
 * @returns {Promise<Object>}
 */
export function createQuote(id, reason, reasonType, quoteTypeId) {
    const query = {
        reason,
        reasonType,
        quoteTypeId,
    };
    return ApiService.post(true, `${baseUrl}/${id}/createQuote`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} reason
 * @returns {Promise<Object>}
 */
export function cancel(id, reason, reasonId) {
    const query = {
        reason,
        reasonId,
    };
    return ApiService.post(true, `${baseUrl}/${id}/cancel`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} priorityId
 * @param {string} reasonId
 * @param {string} reasonIdLabel
 * @param {string} reason
 */
export function changePriority(
    id,
    priorityId,
    reasonId,
    reasonIdLabel,
    reason
) {
    const query = {
        priorityId,
        reasonId,
        reasonIdLabel,
        reason,
    };
    return ApiService.post(true, `${baseUrl}/${id}/changePriority`, {}, query);
}

/**
 *
 * @param {number} id
 * @param {string} ownerId
 * @param {string} coOwnerId
 * @returns {Promise<Object>}
 */
export function transferOwner(id, ownerId, coOwnerId, approvalOwnerId) {
    const query = {
        ownerId,
        ...(coOwnerId && { coOwnerId }),
        ...(approvalOwnerId && { approvalOwnerId }),
    };
    return ApiService.post(true, `${baseUrl}/${id}/transferOwner`, {}, query);
}

export function close(id) {
    return ApiService.post(true, `${baseUrl}/${id}/close`, {});
}

/**
 *
 * @param {array} workOrderIds
 * @param {object} data
 * @returns {Promise<Object>}
 */
export function bulkEdit(data) {
    return ApiService.post(true, `${baseUrl}/bulkEdit`, data);
}

/* DEPRECATED
 * -=-=-=-=-=-=-=-=-= UTILITIES =-=-=-=-=-=-=-=--=-=-=-
 */

/**
 * synchronously validates a work order object
 * @param {object} workOrder
 * @returns {boolean}
 */
export function isvalid_DEPRECATED(workOrder) {
    if (!workOrder) {
        return false;
    }
    const {
        documentTypeId,
        workOrderTypeId,
        priorityId,
        tradeTypeId,
        tradeSubTypeId,
        propertyId,
        opsJobManager,
        clientId,
        contacts,
        occupied,
    } = workOrder;
    if (!documentTypeId || documentTypeId !== DOCUMENT_TYPE.WORK_ORDER) {
        return false;
    }
    if (!workOrderTypeId) {
        return false;
    }
    if (!priorityId) {
        return false;
    }
    if (!tradeTypeId) {
        return false;
    }
    if (!tradeSubTypeId) {
        return false;
    }
    if (!propertyId) {
        return false;
    }
    if (!opsJobManager) {
        return false;
    }
    if (!clientId) {
        return false;
    }
    if (
        contacts[0] &&
        !isEmpty(Utils.deleteEmptyKeys(contacts[0])) &&
        !DocumentService.isValidContact(contacts[0])
    ) {
        return false;
    }
    if (occupied && !contacts[0]) {
        return false;
    }
    if (occupied && !DocumentService.isValidContact(contacts[0])) {
        return false;
    }
    if (occupied && !contacts[0].email && !contacts[0].mobile) {
        return false;
    }
    if (tradeSubTypeId) {
        if (!contacts[0]) {
            return false;
        }
        if (!DocumentService.isValidContact(contacts[0])) {
            return false;
        }
        if (!contacts[0].email && !contacts[0].mobile) {
            return false;
        }
    }
    return true;
}

/*
 * -=-=-=-=-=-=-=-=-= UTILITIES =-=-=-=-=-=-=-=--=-=-=-
 */

/**
 * synchronously validates a work order object
 * @param {object} workOrder
 * @returns {boolean}
 */
export function isValid(workOrder, deprecated = false) {
    if (deprecated) return isvalid_DEPRECATED(workOrder);
    if (!workOrder) {
        return false;
    }
    const {
        documentTypeId,
        workOrderTypeId,
        priorityId,
        tradeTypeId,
        tradeSubTypeId,
        propertyId,
        opsJobManager,
        clientId,
        contacts,
    } = workOrder;
    if (!documentTypeId || documentTypeId !== DOCUMENT_TYPE.WORK_ORDER) {
        return false;
    }
    if (!workOrderTypeId) {
        return false;
    }
    if (!priorityId) {
        return false;
    }
    if (!tradeTypeId) {
        return false;
    }
    if (!tradeSubTypeId) {
        return false;
    }
    if (!propertyId) {
        return false;
    }
    if (!opsJobManager) {
        return false;
    }
    if (!clientId) {
        return false;
    }
    if (
        !contacts.length ||
        contacts.some(c => !DocumentService.isValidContact(c))
    ) {
        return false;
    }
    if (!contacts.some(c => c.primaryContact)) {
        return false;
    }
    return true;
}

export function isValidWarranty(workOrder) {
    if (isValid(workOrder)) {
        const { contractorId, contractorTeamMemberId, documentOrigination } =
            workOrder;

        if (!contractorId) {
            if (contractorId !== null && typeof contractorId !== 'number') {
                return false;
            }
        }

        if (!contractorTeamMemberId) {
            if (
                contractorTeamMemberId !== null &&
                typeof contractorTeamMemberId !== 'number'
            ) {
                return false;
            }
        }
        if (!documentOrigination) {
            return false;
        }
        return true;
    }
    return false;
}

/**
 * returns a boolean if a quote will be created for a given work order
 * @param {Number} payoutClipLevel
 * @param {String} workOrderTypeId
 * @param {Object[]} lineItems
 * @returns {Boolean}
 */
export function workOrderSubmitActionGeneratesQuote(
    payoutClipLevel,
    workOrderTypeId,
    lineItems
) {
    const allLineItems = lineItems || [];
    const createdLineItems = allLineItems.filter(l => l.statusId === 'CREATED');
    const total = sumPayoutTotal(allLineItems);
    const overPayoutClip = total > payoutClipLevel;
    const noLineItemsAdded = allLineItems.filter(l => !l.system).length === 0;
    const _includesOrderedEquipment =
        includesOrderedEquipment(createdLineItems);

    if (noLineItemsAdded) return false;
    if (overPayoutClip && createdLineItems.length) return true;
    if (!overPayoutClip && _includesOrderedEquipment) return true;

    return false;
}

/**
 * returns string to be displayed in Work Order Submit action modal
 * @param {Number} payoutClipLevel
 * @param {String} workOrderTypeId
 * @param {Object[]} lineItems
 * @returns {String}
 */
export function workOrderSubmitActionDetails(
    payoutClipLevel,
    workOrderTypeId,
    lineItems
) {
    const allLineItems = lineItems || [];
    const total = sumPayoutTotal(allLineItems);
    const overPayoutClip = total > payoutClipLevel;
    const lineItemsAdded = allLineItems.filter(l => !l.system).length !== 0;
    const isRR =
        workOrderTypeId === WORK_ORDER_TYPE.REPAIR ||
        workOrderTypeId === WORK_ORDER_TYPE.REPLACE;

    // no line items added
    if (!lineItemsAdded) {
        if (isRR) {
            return 'This work order will be completed and we will initiate payment for the work performed.';
        }
        return (
            'You have not added any line items to this work order.  ' +
            'If line items are not needed this work order will be completed and we will pay you for the work performed.'
        );
    }
    // line items added
    // over clip
    if (overPayoutClip) {
        if (isRR) {
            return (
                'This work order will be completed and we will initiate payment for the work performed. ' +
                'A quote will be created with the other line items added to this work order.'
            );
        }
        return (
            'The line items on this work order are over the clip level. ' +
            'This work order will be completed and we will initiate payment for the work performed. ' +
            'A quote will be submitted for all remaining line items.'
        );
    }
    // under clip
    if (includesOrderedEquipment(allLineItems)) {
        return (
            'The line items on this work order are under the clip but one or more items must be ordered by Motili. ' +
            'This work order will be completed and we will initiate payment for the work performed. ' +
            'A quote will be submitted for ordered line items.'
        );
    }
    return (
        'The line items on this work order are under the clip level. ' +
        'This work order will be completed and we will initiate payment for all equipment and labor.'
    );
}

/**
 * @function isUnderClip
 * @param  {number} payoutClipLevel
 * @param  {object} lineItems
 * @return {boolean}
 */
export function isUnderClip(payoutClipLevel, lineItems) {
    return sumPayoutTotal(lineItems || []) < payoutClipLevel;
}

/**
 * maps truck store fulfillment details to order object
 * @param {Object} workOrder
 * @param {Object} fulfillmentDetails
 * @param {String} fulfillmentDetails.fulfillmentToAddress
 * @param {String} fulfillmentDetails.fulfillmentToCity
 * @param {String} fulfillmentDetails.fulfillmentToRegion
 * @param {String} fulfillmentDetails.fulfillmentToPostalCode
 * @param {String} fulfillmentDetails.fulfillmentToStdCounty
 * @param {String} fulfillmentDetails.fulfillmentToStdCountry
 * @param {String} fulfillmentDetails.fulfillmentTypeId
 * @param {String} fulfillmentDetails.fulfillmentTo
 * @param {Number} fulfillmentDetails.distributionCenterId
 * @returns {Object} workOrder
 */
export function mapFulfillmentDetailsFromTruck(workOrder, fulfillmentDetails) {
    return {
        ...workOrder,
        fulfillmentDetails: {
            ...workOrder.fulfillmentDetails,
            type: fulfillmentDetails.fulfillmentTypeId,
            typeDisplay:
                fulfillmentDetails.fulfillmentTypeDisplay ||
                fulfillmentDetails.fulfillmentTypeId,
            distributionCenterId: fulfillmentDetails.distributionCenterId,
            deliveryLine: fulfillmentDetails.fulfillmentToAddress,
            city: fulfillmentDetails.fulfillmentToCity,
            region: fulfillmentDetails.fulfillmentToRegion,
            postalCode: fulfillmentDetails.fulfillmentToPostalCode,
            name: fulfillmentDetails.fulfillmentTo,
            country:
                fulfillmentDetails.fulfillmentToCountry ||
                fulfillmentDetails.fulfillmentToStdCountry,
        },
    };
}

/**
 * isAssetTaggingRequired
 * @param {Object[] | undefined} lineItems
 * @returns {boolean}
 */
export function isAssetTaggingRequired(lineItems) {
    let required = false;
    if (lineItems) {
        lineItems.forEach(lineItem => {
            if (
                lineItem.sku === 'MHVC-RES-LFEE-V3ASTTGFE' ||
                lineItem.sku === 'MHVC-COM-LFEE-V3ASTTGFE' ||
                lineItem.sku === 'MPLMB-RES-LFEE-V3ASTTGFE' ||
                lineItem.sku === 'MPLMB-COM-LFEE-V3ASTTGFE' ||
                lineItem.sku === 'MPLMB-WH-LFEE-V3ASTTGFE' ||
                lineItem.sku === 'MELEC-RES-LFEE-V3ASTTGFE' ||
                lineItem.sku === 'MELEC-COM-LFEE-V3ASTTGFE'
            ) {
                if (lineItem.statusId !== 'COMPLETED') required = true;
            }
        });
        return required;
    }
    return required;
}

/**
 * @function getJobAssetsAndTags
 * @param  {number} workOrderId
 * @return {object[]} jobAssetsAndTags
 */
export async function getJobAssetsAndTags(workOrderId) {
    return ApiService.get(true, `${baseUrl}/${workOrderId}/jobAssetsAndTags`);
}

/**
 * @function getPropertyAssetsAndTags
 * @param  {number} workOrderId
 * @return {object[]} propertyAssetsAndTags
 */
export async function getPropertyAssetsAndTags(workOrderId) {
    return ApiService.get(
        true,
        `${baseUrl}/${workOrderId}/propertyAssetsAndTags`
    );
}

/**
 * assetTaggingRequirementsMet
 * @param {Object} workOrder
 * @param {Object[]} assetConfigs
 * @returns {boolean|string}
 */
export async function assetTaggingRequirementsMet(workOrder, assetConfigs) {
    if (!workOrder) {
        return false;
    }
    const allAssets = await getJobAssetsAndTags(workOrder.id);
    const assets = allAssets.filter(asset => {
        const matchingConfiguration = _find(
            assetConfigs,
            config => config.typeId === asset.type
        );
        // controllers do not follow normal asset flow and should NOT be used to determine
        // if asset tagging is complete
        return (
            matchingConfiguration &&
            !matchingConfiguration.categories.includes('CONTROLLER')
        );
    });

    const isActive = assetTypeId => {
        if (assetTypeId === '$AT_LEAST_ONE') {
            return true;
        }
        if (!assetConfigs || assetConfigs.length === 0) {
            return false;
        }
        const foundAsset = _find(
            assetConfigs,
            assetConf =>
                assetConf.active &&
                assetConf.typeId === assetTypeId &&
                (assetConf.tradeTypeId === workOrder.tradeTypeId ||
                    (assetTypeId === 'BOILER' &&
                        (workOrder.tradeTypeId === 'HVAC' ||
                            workOrder.tradeTypeId === 'PLUMBING')))
        );
        return !!foundAsset;
    };

    const hasTaggedByThisWorkOrder = (asset, workOrderId) => {
        if (!asset || !asset.tags || asset.tags.length === 0 || !workOrderId) {
            return false;
        }
        const taggedByThisWorkOrder = _find(
            asset.tags,
            _tag => _tag.workOrderId === workOrderId
        );
        return !!taggedByThisWorkOrder;
    };
    const assetTagRequirements = filter(
        workOrder.requirements,
        woReq => woReq?.requirementTypeId === 'ASSET_TAG'
    );
    const notMetRequirements = filter(assetTagRequirements, woRequirement => {
        if (!isActive(woRequirement.requirement.assetTypeId)) {
            // this requirement not valid
            return false;
        }
        if (!woRequirement.meetsClientRequirement) {
            return true;
        }

        if (
            !woRequirement.requirement.metRequirementByAssets ||
            woRequirement.requirement.metRequirementByAssets.length === 0
        ) {
            return true;
        }

        const metReqByAssets = filter(
            woRequirement.requirement.metRequirementByAssets,
            a => {
                const _asset = _find(assets, asset => asset.id === a.assetId);
                if (!_asset) {
                    return false;
                }
                return isActive(_asset.type);
            }
        );
        if (!metReqByAssets || metReqByAssets.length === 0) {
            return true;
        }
        return false;
    });
    if (notMetRequirements && notMetRequirements.length > 0) {
        return false;
    }

    // We don't consider inactive asset or asset from different trade type.
    const assetsTiedToProperty =
        assets && assets.length > 0
            ? assets.filter(asset => isActive(asset.type) && !asset.retired)
            : [];
    const completedTags =
        assets && assets.length > 0
            ? assets.filter(
                  asset =>
                      isActive(asset.type) &&
                      !asset.retired &&
                      hasTaggedByThisWorkOrder(asset, workOrder.id)
              )
            : [];

    if (isAssetTaggingRequired(workOrder.lineItems) && !completedTags.length) {
        return false;
    }

    if (
        isAssetTaggingRequired(workOrder.lineItems) &&
        workOrder.workOrderTypeId === WORK_ORDER_TYPE.PREVENTATIVE_MAINTENANCE
    ) {
        if (!completedTags.length) {
            return false;
        }
        let completionCount = 0;
        completedTags.forEach(asset => {
            assetsTiedToProperty.forEach(propertyAsset => {
                if (asset.id === propertyAsset.id) {
                    completionCount += 1;
                }
            });
        });
        if (completionCount !== assetsTiedToProperty.length) {
            return false;
        }
    }
    if (workOrder.workOrderTypeId === WORK_ORDER_TYPE.REPLACE) {
        // At lease 1 ADD NEW ASSET required. (re-tagged existing asset not met requirement)
        const newAssets = assets.filter(
            asset =>
                !asset.retired &&
                asset.addedByWorkOrderId === workOrder.id &&
                isActive(asset.type)
        );
        if (!newAssets || newAssets.length === 0) {
            return false;
        }
    }

    const reClassificationAssets = allAssets.filter(
        asset =>
            !asset.retired &&
            !_find(
                assetConfigs,
                config => config.active && config.typeId === asset.type
            )
    );
    if (reClassificationAssets.length > 0) {
        return 'RECLASSIFICATION-ERROR';
    }

    return true;
}

/**
 * requirementsMet - returns true if photo requirements met, otherwise false
 * @param {object[]} requirements
 * @param {object[]} clientRequirements
 * @param {object[]} projectRequirements
 * @returns {boolean}
 */
export function requirementsMet(
    requirements,
    clientRequirements,
    projectRequirements = []
) {
    if (!Array.isArray(clientRequirements)) return true;
    let allRequirementsMet = true;
    clientRequirements.forEach(clientRequirement => {
        if (!clientRequirement) return;
        if (
            clientRequirement?.requirementTypeId === 'PHOTO' &&
            !_find(requirements, {
                metClientRequirementId: clientRequirement.id,
            }) &&
            !_find(
                requirements,
                r =>
                    !isVideo(r) &&
                    r.requirement.photoTypeId ===
                        clientRequirement.requirement.id
            )
        ) {
            allRequirementsMet = false;
        } else if (
            clientRequirement?.requirementTypeId === 'VIDEO' &&
            !_find(requirements, {
                metClientRequirementId: clientRequirement.id,
            }) &&
            !_find(
                requirements,
                r =>
                    r.requirement.photoTypeId ===
                    clientRequirement.requirement.id
            )
        ) {
            allRequirementsMet = false;
        } else if (clientRequirement?.requirementTypeId === 'CHECKLIST') {
            const checklistRequirement = _find(requirements, {
                metClientRequirementId: clientRequirement.id,
            });
            if (
                !checklistRequirement ||
                !checklistRequirement.meetsClientRequirement
            ) {
                allRequirementsMet = false;
            }
        }
    });

    _forEach(projectRequirements, projectRequirement => {
        if (!projectRequirement) return;
        if (
            isMediaRequirementType(projectRequirement?.requirementTypeId) &&
            !_find(requirements, {
                metProjectRequirementId: projectRequirement.id,
            })
        ) {
            allRequirementsMet = false;
        } else if (projectRequirement?.requirementTypeId === 'CHECKLIST') {
            const checklistRequirement = _find(requirements, {
                metProjectRequirementId: projectRequirement.id,
            });
            if (
                !checklistRequirement ||
                !checklistRequirement.meetsProjectRequirement
            ) {
                allRequirementsMet = false;
            }
        }
    });
    return allRequirementsMet;

    // function checkPhotoType(clientRequirement) {
    //     // TODO should be able to remove this as of 5/1/18. temp code to deal with migrated photos
    //     let foundMetRequirement = false;
    //     requirements.forEach(requirement => {
    //         if (requirement.requirement.photoTypeId === clientRequirement.requirement.id) {
    //             foundMetRequirement = true;
    //         }
    //     });
    //     return foundMetRequirement;
    // }
}

export function containRejectedPhoto(requirements) {
    return !!requirements.find(
        req =>
            req?.requirementTypeId === 'PHOTO' &&
            req?.approvalStatusId === 'REJECT'
    );
}

/**
 * checkListRequirementsMet - returns true if checklist requirements met, otherwise false
 * @param {object[]} requirements
 * @param {object[]} clientRequirements
 * @param {object[]} projectRequirements
 * @returns {boolean}
 */
export function checkListRequirementsMet(
    requirements,
    clientRequirements,
    projectRequirements = []
) {
    if (
        !Array.isArray(clientRequirements) &&
        !Array.isArray(projectRequirements)
    )
        return true;
    let checkListRequirementsMet = true;
    clientRequirements.forEach(clientRequirement => {
        if (!clientRequirement) return;
        if (clientRequirement?.requirementTypeId === 'CHECKLIST') {
            const checklistRequirement = _find(requirements, {
                metClientRequirementId: clientRequirement.id,
            });
            if (
                !checklistRequirement ||
                !checklistRequirement.meetsClientRequirement
            ) {
                checkListRequirementsMet = false;
            }
        }
    });

    _forEach(projectRequirements, projectRequirement => {
        if (!projectRequirement) return;
        if (projectRequirement?.requirementTypeId === 'CHECKLIST') {
            const checklistRequirement = _find(requirements, {
                metProjectRequirementId: projectRequirement.id,
            });
            if (
                !checklistRequirement ||
                !checklistRequirement.meetsProjectRequirement
            ) {
                checkListRequirementsMet = false;
            }
        }
    });
    return checkListRequirementsMet;
}

/**
 * returns an initialized work order object
 * @param {Object} [options]
 * @param {Number} [options.opsJobManager]
 * @param {String} [options.workOrderTypeId]
 */
export function initWorkOrder(options) {
    const _options = options || {};
    const { opsJobManager, workOrderTypeId, uuid } = _options;
    const inspectionOptions = uuid ? { uuid } : {};
    const nonInspectionOptions = uuid ? {} : { cleanAndCheck: false };
    return {
        ...inspectionOptions,
        ...nonInspectionOptions,
        cleanAndCheck: false,
        documentTypeId: DOCUMENT_TYPE.WORK_ORDER,
        workOrderTypeId: workOrderTypeId || '',
        priorityId: '',
        tradeTypeId: '',
        tradeSubTypeId: '',
        propertyId: '',
        opsJobManager: opsJobManager || 0,
        clipLevel: '',
        checklists: [],
        photoRequirements: [],
        documentOrigination: null,
        occupied: false,
        instructions: {
            lockboxCode: '',
            gateCode: '',
            special: '',
        },
        poNumber: '',
        contacts: [
            {
                typeId: '',
                contactPreferenceId: '',
                notes: '',
                name: '',
                email: '',
                mobile: '',
                mobileCountry: 'US',
                phone: '',
                phoneCountry: 'US',
                validMobile: null,
                primaryContact: false,
                notifications: [],
            },
        ],
        schedule: null,
        extReferenceNumber1: '',
        extReferenceNumber2: '',
        extPoNumber: '',
        clientId: 0,
        contractorId: 0,
        contractorTeamMemberId: 0,
        internalCertificationId: undefined,
    };
}

export function initWarrantyWorkOrder(
    diagnosticWorkOrder,
    userId,
    projectId,
    photoRequirements
) {
    return {
        documentTypeId: DOCUMENT_TYPE.WORK_ORDER,
        workOrderTypeId: 'WARRANTY',
        priorityId: diagnosticWorkOrder.priorityId || '',
        tradeTypeId: diagnosticWorkOrder.tradeTypeId || '',
        tradeSubTypeId: diagnosticWorkOrder.tradeSubTypeId || '',
        propertyId: diagnosticWorkOrder.propertyId || '',
        property: diagnosticWorkOrder.property || null,
        opsJobManager: diagnosticWorkOrder.opsJobManager,
        coOwner: diagnosticWorkOrder.coOwner,
        photoRequirements: photoRequirements || [],
        clipLevel: diagnosticWorkOrder.clipLevel || '',
        documentOrigination: {
            oid: diagnosticWorkOrder.id,
            documentTypeId: diagnosticWorkOrder.documentTypeId,
        },
        occupied: diagnosticWorkOrder.occupied,
        instructions: diagnosticWorkOrder.instructions
            ? {
                  lockboxCode:
                      diagnosticWorkOrder.instructions.lockboxCode || '',
                  gateCode: diagnosticWorkOrder.instructions.gateCode || '',
                  special: diagnosticWorkOrder.instructions.special || '',
              }
            : {
                  lockboxCode: '',
                  gateCode: '',
                  special: '',
              },
        poNumber: diagnosticWorkOrder.poNumber || '',
        contacts: diagnosticWorkOrder.contacts.length
            ? diagnosticWorkOrder.contacts.map(contact => ({
                  typeId: contact.typeId || '',
                  contactPreferenceId: contact.contactPreferenceId || '',
                  notes: contact.notes || '',
                  name: contact.name || '',
                  email: contact.email || '',
                  mobile: contact.mobile || '',
                  mobileCountry: contact.mobileCountry || 'US',
                  phone: contact.phone || '',
                  phoneCountry: contact.phoneCountry || 'US',
                  validPhone: contact.phone ? true : null,
                  validMobile: contact.mobile ? true : null,
                  primaryContact: contact.primaryContact || false,
                  notifications: contact.notifications || [],
              }))
            : [
                  {
                      typeId: '',
                      contactPreferenceId: '',
                      notes: '',
                      name: '',
                      email: '',
                      mobile: '',
                      mobileCountry: 'US',
                      phone: '',
                      phoneCountry: 'US',
                      validMobile: null,
                      primaryContact: false,
                      notifications: [],
                  },
              ],
        schedule: diagnosticWorkOrder.schedule || null,
        extReferenceNumber1: diagnosticWorkOrder.extReferenceNumber1 || '',
        extReferenceNumber2: diagnosticWorkOrder.extReferenceNumber2 || '',
        extPoNumber: diagnosticWorkOrder.extPoNumber || '',
        clientId:
            diagnosticWorkOrder.property && diagnosticWorkOrder.property.client
                ? diagnosticWorkOrder.property.client.id
                : '',
        contractorId: diagnosticWorkOrder.contractorId || null,
        contractorTeamMemberId:
            diagnosticWorkOrder.contractorTeamMemberId || null,
        job: { projectId },
    };
}

export function getClipLevel(clientRequirements) {
    if (!clientRequirements) return 0;
    const clipLevelRequirement = clientRequirements.find(
        cr => cr?.requirementTypeId === 'CLIP_LEVEL'
    );
    if (clipLevelRequirement) {
        return clipLevelRequirement.requirement;
    }
    const defaultClipLevelRequirement = clientRequirements.find(
        cr =>
            cr?.requirementTypeId === 'DEFAULT' &&
            cr?.requirement?.defaultTypeId === 'CLIP_LEVEL'
    );
    if (defaultClipLevelRequirement) {
        return defaultClipLevelRequirement.requirement;
    }
    return 0;
}

export function updateReferenceNumber(workOrderId, referenceToUpdate) {
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/updateReference`,
        referenceToUpdate
    );
}

export function changeProject(workOrderId, projectId) {
    return ApiService.patch(
        true,
        `${baseUrl}/${workOrderId}/changeProject`,
        {},
        { projectId }
    );
}

/**
 * @function isWarrantyJob
 * @param  {type} workOrders {array of work orders in job}
 * @return {type} {true if any work order in the job is a warranty}
 */
export function isWarrantyJob(workOrders) {
    return workOrders.some(
        wo => wo.workOrderTypeId === WORK_ORDER_TYPE.WARRANTY
    );
}

/**
 * validates access token created for processing 'no access' work orders
 * @param workOrderId
 * @returns {Promise}
 */
export function validateWorkOrderToken(workOrderId, actionTokenId) {
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/validate-no-access-token`,
        {
            actionTokenId,
        }
    );
}

/**
 * processes WO for no access confirmation or denial
 *
 * @export
 * @param {number} workOrderId
 * @param {number} actionTokenId
 * @param {boolean} confirmNoAccess
 * @returns
 */
export function processNoAccess(data) {
    return ApiService.put(
        true,
        `${baseUrl}/${data.id}/process-no-access`,
        data
    );
}

export function createDraft(workOrder) {
    return ApiService.post(true, `${baseUrl}/createDraft`, workOrder);
}

export function calculateLineItemCost(id, data) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/lineItems/calculateLineItemCost`,
        data
    );
}

export function generatePdf(
    id,
    fileName,
    statementOfWork,
    selectedLineItemIds,
    checkedAssetIds,
    bundlePricing,
    quotePdfType,
    selectedPhotoIds,
    selectedChecklistIds,
    hideBundleItems,
    hideSerialNumbers
) {
    const query = {
        fileName,
        statementOfWork,
        includePhotos: true,
        download: true,
        email: false,
        selectedLineItemIds,
        checkedAssetIds,
        bundlePricing,
        quotePdfType,
        selectedPhotoIds,
        selectedChecklistIds,
        hideBundleItems,
        hideSerialNumbers,
    };

    return ApiService.post(true, `${baseUrl}/${id}/export`, {}, query);
}

export function declineOnBehalf(id, declineInfo) {
    return ApiService.post(
        true,
        `${baseUrl}/${id}/declineOnBehalf`,
        declineInfo
    );
}

export function updateWorkOrderCheckInsV2(id, checkInId, checkInData) {
    return ApiService.put(true, `${baseUrl}/${id}/checkInsV2/${checkInId}`, {
        ...checkInData,
    });
}

export async function specifyWorkOrderIssue(
    workOrderId,
    issueId,
    comment,
    reasonIds,
    issueData = {}
) {
    const query = { issueId, comment, reasonIds };

    return ApiService.patch(
        true,
        `${baseUrl}/${workOrderId}/specifyIssue`,
        issueData,
        query
    );
}

export async function solveIssueWorkOrderIssue(
    workOrderId,
    issueId,
    comment,
    reasonIds,
    resolvedReasonIds,
    resolvedOtherReason,
    noteMentionsData
) {
    const query = {
        issueId,
        comment,
        reasonIds,
        resolvedReasonIds,
        resolvedOtherReason,
    };

    return ApiService.patch(
        true,
        `${baseUrl}/${workOrderId}/solveIssue`,
        { noteMentionsData },
        query
    );
}

export function getIsNeedToCreateQuote(workOrderId) {
    return ApiService.get(true, `${baseUrl}/isNeedToCreateQuote`, {
        workOrderId,
    });
}

export async function updateWorkOrderIssue(
    workOrderId,
    issueTrackingId,
    issueData = {}
) {
    const query = { issueTrackingId };

    return ApiService.patch(
        true,
        `${baseUrl}/${workOrderId}/updateIssue`,
        issueData,
        query
    );
}

export function fetchWorkOrderIssueTracking(filter) {
    const query = {
        filter,
    };
    return ApiService.get(true, `workOrderIssueTrackings`, query);
}

export function resendContractorNotification(workOrderId) {
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/resendContractorNotification`
    );
}

export async function updateWorkers(workOrderId, workerIds) {
    return ApiService.post(
        true,
        `${baseUrl}/${workOrderId}/workers`,
        workerIds
    );
}

/**
 * @function findContractorReopenWorkOrder
 * @return {object[]} workOrderIds
 */
export async function findContractorReopenWorkOrder(
    onlyWithRejectedPhotoRequirement
) {
    return ApiService.get(
        true,
        `${baseUrl}/findContractorReopenWorkOrder?onlyWithRejectedPhotoRequirement=${onlyWithRejectedPhotoRequirement}`
    );
}

/**
 * get document timestamps
 * @param {number} id
 * @returns {Promise<Object>}
 */
export function getDocumentTimestamps(id) {
    return ApiService.get(true, `${baseUrl}/${id}/documentTimestamps`);
}
/**
 * get document timestamps
 * @param {number} id
 * @returns {Promise<Object>}
 */
export function fetchWorkOrderEvents(id) {
    return ApiService.get(true, `${baseUrl}/${id}/documentEvents`);
}

/**
 * retrieves all workOrder related notes
 * @param {number} jobId
 * @returns {Promise<Array>}
 */
export function getWorkOrderRelatedNotes(jobId) {
    return ApiService.get(true, `${baseUrl}/job/${jobId}/documentNotes`);
}

/**
 * retrieves all workOrder related comments
 * @param {number} jobId
 * @returns {Promise<Array>}
 */
export function getWorkOrderRelatedComments(jobId) {
    return ApiService.get(true, `${baseUrl}/job/${jobId}/documentComments`);
}
